// Simplified service
import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  TicketPricingResponse,
  PaymentRequest,
  PaymentResponse,
} from "../Component/Application/PurchaseTicket/ticket-purchase"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
})

// ✅ Dynamically attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)

const TicketService = {
  // Get current exhibition ticket pricing
  async getTicketPricing(): Promise<TicketPricingResponse> {
    try {
      const response = await api.get("/api/entries/purchase-tickets/pricing/")
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Failed to fetch ticket pricing")
      }
    } catch (error) {
      console.error("Error fetching ticket pricing:", error)
      throw new Error("Failed to fetch ticket pricing information")
    }
  },

  // Submit ticket payment request
  async submitTicketPayment(paymentData: PaymentRequest): Promise<PaymentResponse> {
    try {
      const response = await api.post("/api/entries/purchase-tickets/submit/", paymentData)
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || "Payment submission failed")
      }
    } catch (error) {
      console.error("Error submitting ticket payment:", error)
      throw new Error("An unexpected error occurred during payment processing")
    }
  },

  // Calculate total amount based on ticket price and quantity
  calculateTotal(ticketPrice: number, numTickets: number): number {
    return ticketPrice * numTickets
  },

  // Validate phone number format - Updated for specific Rwanda prefixes
  validatePhoneNumber(phoneNumber: string): boolean {
    const rwandaPhoneRegex = /^(\+250|0)(72|73|78|79)\d{7}$/
    return rwandaPhoneRegex.test(phoneNumber.replace(/\s/g, ""))
  },

  // Format phone number for display
  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, "")
    if (cleaned.startsWith("250")) {
      return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`
    } else if (cleaned.startsWith("0")) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)}`
    }
    return phoneNumber
  },

  // Format currency - Updated to use FRW as default
  formatCurrency(amount: number, currency = "FRW"): string {
    return new Intl.NumberFormat("en-us", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  },
}

export default TicketService
